import React, { useState, useMemo } from 'react';
import { TrendingUp, Users, Briefcase, Clock, FileText } from 'lucide-react';
import EnhancedClientFilter from '../shared/EnhancedClientFilter';
import useClientAccess from '../../hooks/useClientAccess';
import useRecruiterMetrics from '../../hooks/useRecruiterMetrics';
import useAccountManagerMetrics from '../../hooks/useAccountManagerMetrics';
import MetricTile from './MetricTile';
import MetricDetailPane from './MetricDetailPane';
import ErrorBoundary from '../shared/ErrorBoundary';
import { getStatusColorClass } from '../../constants/submissionStatuses';

const ActivitySummaryContent = ({ selectedUser, submissions, jobs, candidates, teamLead, team, allUsers }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('ytd');
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'team'
  const [selectedClients, setSelectedClients] = useState(new Set()); // Multi-select client filter
  const [isInitialized, setIsInitialized] = useState(false); // Track if default clients have been set

  // ============================================================================
  // CRITICAL DEFINITIONS - DO NOT MODIFY WITHOUT UPDATING SPECIFICATION
  // See: /app/CURRENT_PIPELINE_METRICS_SPECIFICATION.md
  // See: /app/CLIENT_VISIBILITY_BUSINESS_LOGIC.md - Client access & pipeline monitoring
  // ============================================================================
  //
  // KEY BUSINESS RULE: Current Submissions/Pipeline is about CLIENT ACCESS, not candidate ownership
  // - Regular recruiters inherit their team lead's assigned clients
  // - This determines which job submissions they see (by job's client company)
  // - primary_recruiter_id on candidates is a secondary factor
  // ============================================================================
  
  // TERMINAL Job Statuses = Jobs closed/completed, no longer recruiting
  // These are EXCLUDED from all Current Pipeline metrics
  const TERMINAL_JOB_STATUSES = ['filled', 'cancelled', 'lost', 'declined', 'on_hold'];
  
  // ============================================================================
  // JOB STATUS CONSTANTS
  // ============================================================================
  
  // ACTIVE Job Statuses = Jobs still open for recruitment (pipeline metrics)
  // Includes jobs from 'accepting_candidates' through 'offer_out'
  // EXCLUDES terminal statuses: filled, closed, declined, lost, cancelled, on_hold
  const ACTIVE_JOB_STATUSES = ['accepting_candidates', 'covered', 'interviewing', 'offer_out'];
  
  // ACTION REQUIRED Job Statuses = Jobs that NEED SUBMISSIONS (Current Open Jobs metric)
  // These are jobs where recruiters should actively be sourcing and submitting candidates
  // EXCLUDES:
  //   - 'covered' → Already have enough candidates, don't need more
  //   - 'offer_out' → In final stages, too late for new submissions
  //   - All terminal statuses → Job is done
  const ACTION_REQUIRED_JOB_STATUSES = ['accepting_candidates', 'interviewing'];

  // ============================================================================
  // USE CUSTOM HOOKS FOR CLIENT ACCESS AND METRICS CALCULATION
  // ============================================================================
  // Extract client access logic and role detection
  const clientAccess = useClientAccess(selectedUser, teamLead, jobs);
  const { 
    assignedClients: allowedClients, 
    allClients: allClientsFromJobs,
    accessType,
    isAccountManager, 
    isLeadRecruiter 
  } = clientAccess;

  // Get Account Manager metrics (if user is an AM)
  const {
    accountManagerJobs,
    accountManagerClients,
    amCurrentOpenJobs,
    amActiveSubs,
    amTimeFilteredJobs,
    amFilledJobs
  } = useAccountManagerMetrics({
    jobs,
    submissions,
    selectedUser,
    selectedClients,
    selectedTimeframe
  });

  // Get Recruiter metrics (if user is NOT an AM)
  const {
    currentOpenJobsMetric,
    currentPipelineMetrics,
    activityMetrics
  } = useRecruiterMetrics({
    jobs,
    submissions,
    candidates,
    selectedUser,
    team,
    allUsers,
    allowedClients,
    selectedClients,
    selectedTimeframe,
    viewMode,
    isLeadRecruiter
  });

  // ============================================================================
  // COMBINED METRICS FOR UI RENDERING
  // ============================================================================
  // Combine metrics from hooks for the detail pane

  // Combined metrics for detail pane
  const allMetrics = useMemo(() => ({
    currentOpenJobs: currentOpenJobsMetric,
    ...currentPipelineMetrics,
    ...activityMetrics
  }), [currentOpenJobsMetric, currentPipelineMetrics, activityMetrics]);

  const timeframeOptions = [
    { value: 'ytd', label: 'YTD' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_12_months', label: 'Last 12 Months' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100',
        gradient: 'from-blue-50 to-blue-100'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-100',
        gradient: 'from-indigo-50 to-indigo-100'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-100',
        gradient: 'from-yellow-50 to-yellow-100'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100',
        gradient: 'from-purple-50 to-purple-100'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-600',
        hover: 'hover:bg-green-100',
        gradient: 'from-green-50 to-green-100'
      }
    };
    return colors[color] || colors.blue;
  };

  // Return Account Manager UI if user is an account manager
  if (isAccountManager) {
    return (
      <div className="space-y-6">
        {/* Client Filter */}
        {clientAccess.assignedClients.length > 0 && (
          <div className="mb-6">
            <EnhancedClientFilter
              assignedClients={clientAccess.assignedClients}
              allClients={clientAccess.allClients}
              selectedClients={selectedClients}
              onSelectionChange={setSelectedClients}
              accessType={clientAccess.accessType}
              userRole={selectedUser?.role || selectedUser?.roles?.[0]}
            />
          </div>
        )}

        {/* Current Open Jobs */}
        <div 
          onClick={() => setSelectedMetric(selectedMetric === 'am_currentOpenJobs' ? null : 'am_currentOpenJobs')}
          className={`cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100 border-2 ${
            selectedMetric === 'am_currentOpenJobs' ? 'border-blue-500 ring-4 ring-blue-200' : 'border-blue-200'
          } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
          data-testid="metric-am-currentOpenJobs"
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Current Open Jobs</h4>
              <p className="text-sm text-gray-600 mt-1">Jobs needing submissions (excludes covered, offer out)</p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <span className="text-5xl font-bold text-blue-600">{amCurrentOpenJobs.length}</span>
          </div>
        </div>

        {/* Current Pipeline */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Current Pipeline</h4>
          <p className="text-sm text-gray-600">
            {isAccountManager ? 'Submission activity for my active jobs' : 'All my submission activity'}
          </p>
          
          <div className="grid grid-cols-5 gap-4">
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'submission' ? null : 'submission')}
              className={`cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100 border-2 ${
                selectedMetric === 'submission' ? 'border-blue-500 ring-4 ring-blue-200' : 'border-blue-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-submission"
            >
              <p className="text-sm font-medium text-gray-700">Submission</p>
              <p className="text-4xl font-bold text-blue-600 mt-2">
                {(isAccountManager ? amActiveSubs : submissions).filter(s => s.status === 'Submission').length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_clientSubmission' ? null : 'am_clientSubmission')}
              className={`cursor-pointer bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 ${
                selectedMetric === 'am_clientSubmission' ? 'border-indigo-500 ring-4 ring-indigo-200' : 'border-indigo-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-clientSubmission"
            >
              <p className="text-sm font-medium text-gray-700">Client Submission</p>
              <p className="text-4xl font-bold text-indigo-600 mt-2">
                {(isAccountManager ? amActiveSubs : submissions).filter(s => s.status === 'Client Submission').length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_interview' ? null : 'am_interview')}
              className={`cursor-pointer bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 ${
                selectedMetric === 'am_interview' ? 'border-yellow-500 ring-4 ring-yellow-200' : 'border-yellow-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-interview"
            >
              <p className="text-sm font-medium text-gray-700">Interview</p>
              <p className="text-4xl font-bold text-yellow-600 mt-2">
                {(isAccountManager ? amActiveSubs : submissions).filter(s => s.status === 'Interview').length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_offerExtended' ? null : 'am_offerExtended')}
              className={`cursor-pointer bg-gradient-to-br from-purple-50 to-purple-100 border-2 ${
                selectedMetric === 'am_offerExtended' ? 'border-purple-500 ring-4 ring-purple-200' : 'border-purple-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-offerExtended"
            >
              <p className="text-sm font-medium text-gray-700">Offer Extended</p>
              <p className="text-4xl font-bold text-purple-600 mt-2">
                {(isAccountManager ? amActiveSubs : submissions).filter(s => s.status === 'Offer Extended').length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_onboarding' ? null : 'am_onboarding')}
              className={`cursor-pointer bg-gradient-to-br from-green-50 to-green-100 border-2 ${
                selectedMetric === 'am_onboarding' ? 'border-green-500 ring-4 ring-green-200' : 'border-green-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-onboarding"
            >
              <p className="text-sm font-medium text-gray-700">Onboarding</p>
              <p className="text-4xl font-bold text-green-600 mt-2">
                {(isAccountManager ? amActiveSubs : submissions).filter(s => s.status === 'Offer Accepted').length}
              </p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
          </div>
        </div>

        {/* Activity Metrics */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
              <h4 className="text-lg font-semibold text-gray-900">Activity Metrics</h4>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedTimeframe('ytd');
                  setSelectedMetric(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === 'ytd'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                YTD
              </button>
              <button
                onClick={() => {
                  setSelectedTimeframe('this_month');
                  setSelectedMetric(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === 'this_month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                This Month
              </button>
              <button
                onClick={() => {
                  setSelectedTimeframe('last_month');
                  setSelectedMetric(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === 'last_month'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Last Month
              </button>
              <button
                onClick={() => {
                  setSelectedTimeframe('last_6_months');
                  setSelectedMetric(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === 'last_6_months'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Last 6 Months
              </button>
              <button
                onClick={() => {
                  setSelectedTimeframe('last_12_months');
                  setSelectedMetric(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTimeframe === 'last_12_months'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Last 12 Months
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_jobsAdded' ? null : 'am_jobsAdded')}
              className={`cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100 border-2 ${
                selectedMetric === 'am_jobsAdded' ? 'border-blue-500 ring-4 ring-blue-200' : 'border-blue-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-jobsAdded"
            >
              <p className="text-sm font-medium text-gray-700">Jobs Added by Me</p>
              <p className="text-4xl font-bold text-blue-600 mt-2">{amTimeFilteredJobs.length}</p>
              <p className="text-xs text-gray-500 mt-1">Jobs I'm credited as Account Manager</p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
            <div 
              onClick={() => setSelectedMetric(selectedMetric === 'am_jobsFilled' ? null : 'am_jobsFilled')}
              className={`cursor-pointer bg-gradient-to-br from-green-50 to-green-100 border-2 ${
                selectedMetric === 'am_jobsFilled' ? 'border-green-500 ring-4 ring-green-200' : 'border-green-200'
              } rounded-lg p-6 transition-all hover:shadow-lg hover:scale-105`}
              data-testid="metric-am-jobsFilled"
            >
              <p className="text-sm font-medium text-gray-700">Jobs Successfully Placed</p>
              <p className="text-4xl font-bold text-green-600 mt-2">{amFilledJobs.length}</p>
              <p className="text-xs text-gray-500 mt-1">Jobs I successfully filled</p>
              <p className="text-xs text-gray-400 mt-2">Click to view details</p>
            </div>
          </div>
        </div>

        {/* Detail Pane for Account Managers */}
        {selectedMetric && selectedMetric.startsWith('am_') && (
          <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
            <div className={`px-6 py-4 border-b border-gray-200 ${
              selectedMetric === 'am_currentOpenJobs' ? 'bg-gradient-to-r from-blue-100 to-blue-200' :
              selectedMetric === 'am_clientSubmission' ? 'bg-gradient-to-r from-indigo-100 to-indigo-200' :
              selectedMetric === 'am_interview' ? 'bg-gradient-to-r from-yellow-100 to-yellow-200' :
              selectedMetric === 'am_offerExtended' ? 'bg-gradient-to-r from-purple-100 to-purple-200' :
              selectedMetric === 'am_onboarding' ? 'bg-gradient-to-r from-green-100 to-green-200' :
              selectedMetric === 'am_jobsAdded' ? 'bg-gradient-to-r from-blue-100 to-blue-200' :
              selectedMetric === 'am_jobsFilled' ? 'bg-gradient-to-r from-green-100 to-green-200' :
              'bg-gray-100'
            }`}>
              <h4 className="text-lg font-semibold text-gray-900">
                {selectedMetric === 'am_currentOpenJobs' && 'Current Open Jobs - Details'}
                {selectedMetric === 'am_clientSubmission' && 'Client Submission - Details'}
                {selectedMetric === 'am_interview' && 'Interview - Details'}
                {selectedMetric === 'am_offerExtended' && 'Offer Extended - Details'}
                {selectedMetric === 'am_onboarding' && 'Onboarding - Details'}
                {selectedMetric === 'am_jobsAdded' && 'Jobs Added by Me - Details'}
                {selectedMetric === 'am_jobsFilled' && 'Jobs Successfully Placed - Details'}
              </h4>
              <p className="text-sm text-gray-600">
                {selectedMetric === 'am_currentOpenJobs' && `${amCurrentOpenJobs.length} job${amCurrentOpenJobs.length !== 1 ? 's' : ''}`}
                {selectedMetric === 'am_clientSubmission' && `${amActiveSubs.filter(s => s.status === 'Client Submission').length} submission${amActiveSubs.filter(s => s.status === 'Client Submission').length !== 1 ? 's' : ''}`}
                {selectedMetric === 'am_interview' && `${amActiveSubs.filter(s => s.status === 'Interview').length} submission${amActiveSubs.filter(s => s.status === 'Interview').length !== 1 ? 's' : ''}`}
                {selectedMetric === 'am_offerExtended' && `${amActiveSubs.filter(s => s.status === 'Offer Extended').length} submission${amActiveSubs.filter(s => s.status === 'Offer Extended').length !== 1 ? 's' : ''}`}
                {selectedMetric === 'am_onboarding' && `${amActiveSubs.filter(s => s.status === 'Offer Accepted').length} submission${amActiveSubs.filter(s => s.status === 'Offer Accepted').length !== 1 ? 's' : ''}`}
                {selectedMetric === 'am_jobsAdded' && `${amTimeFilteredJobs.length} job${amTimeFilteredJobs.length !== 1 ? 's' : ''} in selected timeframe`}
                {selectedMetric === 'am_jobsFilled' && `${amFilledJobs.length} job${amFilledJobs.length !== 1 ? 's' : ''} in selected timeframe`}
              </p>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              {/* Show Jobs for job-related metrics */}
              {(selectedMetric === 'am_currentOpenJobs' || selectedMetric === 'am_jobsAdded' || selectedMetric === 'am_jobsFilled') && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {selectedMetric === 'am_currentOpenJobs' && 'Date Added'}
                        {selectedMetric === 'am_jobsAdded' && 'Date Added'}
                        {selectedMetric === 'am_jobsFilled' && 'Placed Date'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      let jobsToShow = [];
                      if (selectedMetric === 'am_currentOpenJobs') jobsToShow = amCurrentOpenJobs;
                      if (selectedMetric === 'am_jobsAdded') jobsToShow = amTimeFilteredJobs;
                      if (selectedMetric === 'am_jobsFilled') jobsToShow = amFilledJobs;
                      
                      return jobsToShow.map((job, index) => (
                        <tr key={job.id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{job.title || 'Untitled'}</div>
                            <div className="text-xs text-gray-500">{job.external_id || ''}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {job.client_company || job.client || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {job.location || job.city || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {job.date_added ? new Date(job.date_added).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              selectedMetric === 'am_jobsFilled' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {job.status || 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              )}

              {/* Show Submissions for submission-related metrics */}
              {(selectedMetric === 'am_clientSubmission' || selectedMetric === 'am_interview' || 
                selectedMetric === 'am_offerExtended' || selectedMetric === 'am_onboarding') && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(() => {
                      let subsToShow = [];
                      if (selectedMetric === 'am_clientSubmission') subsToShow = amActiveSubs.filter(s => s.status === 'Client Submission');
                      if (selectedMetric === 'am_interview') subsToShow = amActiveSubs.filter(s => s.status === 'Interview');
                      if (selectedMetric === 'am_offerExtended') subsToShow = amActiveSubs.filter(s => s.status === 'Offer Extended');
                      if (selectedMetric === 'am_onboarding') subsToShow = amActiveSubs.filter(s => s.status === 'Offer Accepted');
                      
                      return subsToShow.map((submission, index) => {
                        const job = jobs.find(j => j.id === submission.job_id);
                        return (
                          <tr key={submission.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">
                                {submission.candidate || submission.candidate_name || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-700">{job?.title || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{job?.external_id || ''}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {job?.client_company || job?.client || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {submission.date_added ? new Date(submission.date_added).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                selectedMetric === 'am_clientSubmission' ? 'bg-indigo-100 text-indigo-700' :
                                selectedMetric === 'am_interview' ? 'bg-yellow-100 text-yellow-700' :
                                selectedMetric === 'am_offerExtended' ? 'bg-purple-100 text-purple-700' :
                                selectedMetric === 'am_onboarding' ? 'bg-green-100 text-green-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {submission.status || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              )}
              
              {/* Empty state */}
              {(() => {
                let count = 0;
                if (selectedMetric === 'am_currentOpenJobs') count = amCurrentOpenJobs.length;
                if (selectedMetric === 'am_clientSubmission') count = amActiveSubs.filter(s => s.status === 'Client Submission').length;
                if (selectedMetric === 'am_interview') count = amActiveSubs.filter(s => s.status === 'Interview').length;
                if (selectedMetric === 'am_offerExtended') count = amActiveSubs.filter(s => s.status === 'Offer Extended').length;
                if (selectedMetric === 'am_onboarding') count = amActiveSubs.filter(s => s.status === 'Offer Accepted').length;
                if (selectedMetric === 'am_jobsAdded') count = amTimeFilteredJobs.length;
                if (selectedMetric === 'am_jobsFilled') count = amFilledJobs.length;
                
                return count === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <p>No data available</p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle - Only for Lead Recruiters */}
      {isLeadRecruiter && team && (
        <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <span className="text-sm font-semibold text-gray-900">View Mode:</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setViewMode('individual');
                setSelectedMetric(null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'individual'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
              data-testid="view-mode-individual"
            >
              My Metrics
            </button>
            <button
              onClick={() => {
                setViewMode('team');
                setSelectedMetric(null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'team'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
              data-testid="view-mode-team"
            >
              Team Metrics
              {team.team_members && (
                <span className="ml-1 text-xs opacity-75">
                  ({team.team_members.length + 1} people)
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Client Filter - For all users */}
      {clientAccess.assignedClients.length > 0 && (
        <div className="mb-6">
          <EnhancedClientFilter
            assignedClients={clientAccess.assignedClients}
            allClients={clientAccess.allClients}
            selectedClients={selectedClients}
            onSelectionChange={setSelectedClients}
            accessType={clientAccess.accessType}
            userRole={selectedUser?.role || selectedUser?.roles?.[0]}
          />
        </div>
      )}

      {/* Current Open Jobs - Standalone Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full"></div>
          <h4 className="text-lg font-semibold text-gray-900">Current Open Jobs</h4>
          <span className="text-xs text-gray-500 ml-2">(Excludes covered, matches Jobs tab default)</span>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          <MetricTile
            metric={currentOpenJobsMetric}
            metricKey="currentOpenJobs"
            isSelected={selectedMetric === 'currentOpenJobs'}
            onSelect={(key) => setSelectedMetric(selectedMetric === key ? null : key)}
            testId="metric-currentOpenJobs"
          />
        </div>
      </div>

      {/* Current Submissions/Pipeline Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
          <h4 className="text-lg font-semibold text-gray-900">Current Submissions/Pipeline</h4>
        </div>
        
        {/* Row 1: Happy Path (Chronological) */}
        <div>
          <p className="text-xs text-gray-500 mb-2 font-medium">Active Pipeline (Chronological)</p>
          <div className="grid grid-cols-5 gap-4">
            {['submission', 'clientSubmission', 'interview', 'offerExtended', 'offerAccepted'].map((key) => (
              <MetricTile
                key={key}
                metric={currentPipelineMetrics[key]}
                metricKey={key}
                isSelected={selectedMetric === key}
                onSelect={(k) => setSelectedMetric(selectedMetric === k ? null : k)}
                testId={`metric-${key}`}
              />
            ))}
          </div>
        </div>

        {/* Row 2: Terminal States */}
        <div>
          <p className="text-xs text-gray-500 mb-2 font-medium">Terminal States</p>
          <div className="grid grid-cols-5 gap-4">
            {['onboardingInitiated', 'onboarded', 'rejected', 'interviewRejected', 'offerRejected'].map((key) => (
              <MetricTile
                key={key}
                metric={currentPipelineMetrics[key]}
                metricKey={key}
                isSelected={selectedMetric === key}
                onSelect={(k) => setSelectedMetric(selectedMetric === k ? null : k)}
                testId={`metric-${key}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Timeframe Selector - Between Pipeline and Activity */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Activity Summary Timeframe:</span>
        </div>
        
        <div className="flex gap-2">
          {timeframeOptions.map(option => (
            <button
              key={option.value}
              onClick={() => {
                setSelectedTimeframe(option.value);
                setSelectedMetric(null); // Reset selected metric when timeframe changes
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedTimeframe === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity Metrics Section - Time Filtered */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
          <h4 className="text-lg font-semibold text-gray-900">Activity Metrics</h4>
          <span className="text-xs text-gray-500 ml-2">
            ({timeframeOptions.find(opt => opt.value === selectedTimeframe)?.label})
          </span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(activityMetrics).map(([key, metric]) => (
            <MetricTile
              key={key}
              metric={metric}
              metricKey={key}
              isSelected={selectedMetric === key}
              onSelect={(k) => setSelectedMetric(selectedMetric === k ? null : k)}
              testId={`metric-${key}`}
            />
          ))}
        </div>
      </div>

      {/* Detail Pane */}
      {selectedMetric && allMetrics[selectedMetric] && (
        <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
          <div className={`bg-gradient-to-r ${getColorClasses(allMetrics[selectedMetric].color).gradient} px-6 py-4 border-b border-gray-200`}>
            <h4 className="text-lg font-semibold text-gray-900">
              {allMetrics[selectedMetric].label} - Details
            </h4>
            <p className="text-sm text-gray-600">
              {allMetrics[selectedMetric].count} item{allMetrics[selectedMetric].count !== 1 ? 's' : ''} in selected timeframe
            </p>
          </div>

          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            {(selectedMetric === 'jobsSubmittedTo' || selectedMetric === 'currentOpenJobs' || selectedMetric === 'jobsFilled') ? (
              // Show jobs
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    {selectedMetric === 'jobsSubmittedTo' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                    )}
                    {selectedMetric === 'jobsFilled' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Placed</th>
                    )}
                    {selectedMetric === 'currentOpenJobs' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allMetrics[selectedMetric].data.map((job, index) => {
                    // Get submissions for this job based on context (AM vs recruiter)
                    const relevantSubmissions = isAccountManager ? amActiveSubs : submissions;
                    const jobSubmissions = relevantSubmissions.filter(sub => sub.job_id === job.id);
                    return (
                      <tr key={job.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{job.title || 'Untitled'}</div>
                          <div className="text-xs text-gray-500">{job.external_id || ''}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {job.client_company || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {job.location || job.city || 'N/A'}
                        </td>
                        {selectedMetric === 'jobsSubmittedTo' && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {jobSubmissions.length}
                          </td>
                        )}
                        {selectedMetric === 'jobsFilled' && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {relevantSubmissions.filter(sub => sub.job_id === job.id && sub.status === 'Onboarded').length}
                          </td>
                        )}
                        {selectedMetric === 'currentOpenJobs' && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {job.date_added ? new Date(job.date_added).toLocaleDateString() : 'N/A'}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedMetric === 'currentOpenJobs' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {job.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              // Show submissions
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allMetrics[selectedMetric].data.map((submission, index) => {
                    const job = jobs.find(j => j.id === submission.job_id);
                    return (
                      <tr key={submission.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.candidate || submission.candidate_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-700">{job?.title || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{job?.client_company || ''}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {submission.date_added ? new Date(submission.date_added).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedMetric === 'jobsFilled' ? 'bg-green-100 text-green-700' :
                            getStatusColorClass(selectedMetric)
                          }`}>
                            {submission.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            
            {allMetrics[selectedMetric].count === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p>No data available for this timeframe</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap with Error Boundary to prevent crashes
const ActivitySummary = (props) => (
  <ErrorBoundary
    title="Dashboard Error"
    message="Unable to load the activity summary. This might be due to a temporary issue."
  >
    <ActivitySummaryContent {...props} />
  </ErrorBoundary>
);

export default ActivitySummary;
